import { Alert, Box, Stack, Typography } from "@mui/material";
import {
  bulkMarkingInstruction,
  DotMarkingContainer,
  DotMarkingGroup,
  dotMarkingGroupFor,
  packageLabelInstruction,
  requiresHazardLabel,
  requiresLargeIdNumber,
} from "encamp-shared/src/waste/dotMarkingGroup";
import {
  diamondForHazardClass,
  HazardClassDiamondEntry,
  printsRegulatedHazardLabel,
} from "encamp-shared/src/waste/hazardClassDiamond";
import {
  CAPTION_SIZE_AT_REFERENCE,
  CHIP_TEXT_SIZE_AT_REFERENCE,
  DIAMOND_NUMBER_SIZE_AT_REFERENCE,
  DIAMOND_SIZE_AT_REFERENCE,
  FIELD_LABEL_LETTER_SPACING_AT_REFERENCE,
  FIELD_LABEL_SIZE_AT_REFERENCE,
  HEADER_COLOR,
  HEADER_PADDING_VERTICAL_AT_REFERENCE,
  HEADER_TEXT_COLOR,
  HEADER_TITLE_SIZE_AT_REFERENCE,
  HEADER_WARNING_LEAD_SIZE_AT_REFERENCE,
  HEADER_WARNING_REMAINDER_SIZE_AT_REFERENCE,
  ID_SHIPPING_NAME_SIZE_AT_REFERENCE,
  fitHeaderTitleSize,
  HEADER_TITLE_WIDTH_AT_REFERENCE,
  headerCarriesLegend,
  headerTitleBoxWidth,
  INHALATION_HAZARD_SIZE_AT_REFERENCE,
  labelHeaderFor,
  LABEL_REFERENCE_WIDTH,
  MUTED_TEXT_COLOR,
  ORIENTATION_ARROWS_SIZE_AT_REFERENCE,
  packingGroupClause,
  poisonInhalationLine,
  QR_SIZE_AT_REFERENCE,
  RCRA_PICTOGRAM_SIZE_AT_REFERENCE,
  RIGHT_COLUMN_WIDTH_AT_REFERENCE,
  RIGHT_COLUMN_WIDTH_RATIO,
  RULE_COLOR,
  SECONDARY_LINE_SIZE_AT_REFERENCE,
  SECONDARY_TEXT_COLOR,
  SUBSIDIARY_DIAMOND_SIZE_AT_REFERENCE,
  VALUE_SIZE_AT_REFERENCE,
} from "encamp-shared/src/waste/hazardousWasteLabelVisuals";
import {
  LabelContentFieldsFragment,
  LabelFormat,
  PhysicalState,
  UnitType,
} from "generated-graphql/graphql";
import { ReactElement } from "react";

import EncampLogo from "../../../../../assets/EncampLogo.svg";
import { isDotProfileIncomplete, qrImageUrl } from "./labelPreviewContent";
import { OrientationArrows, PreviewHazardLabel } from "./LabelPreviewDiamond";
import { LeftColumn } from "./LabelPreviewFields";
import { HeaderMetrics, SmTextMetrics } from "./labelPreviewMetrics";
import { LABEL_WIDTH_BY_SIZE } from "./previewZoom";

// A pure knockout-background color for the branded QR overlay -- not shared
// with pdf-generator's qrSvg.tsx (its equivalent is an unexported literal
// there too), so it stays local rather than round-tripping through the
// shared visuals module for a value neither side names.
const KNOCKOUT_BACKGROUND_COLOR = "#FFFFFF";

/** QR size for the non-`fixedWidth` (embedded, e.g. ContainerLabelSection's
 * inline summary) rendering -- unchanged from before this proportion pass,
 * since that usage isn't the fixed-artifact "preview a print job" surface
 * this pass targets and stays free to flex with its container. */
const QR_SIZE_BY_SIZE: Record<"sm" | "lg", number> = { sm: 96, lg: 160 };

/** Hazard-diamond size for the non-`fixedWidth` rendering -- same rationale
 * as QR_SIZE_BY_SIZE above. */
const DEFAULT_DIAMOND_SIZE = 56;

export interface LabelPreviewProps {
  content: LabelContentFieldsFragment;
  layout: LabelFormat;
  branded: boolean;
  /** Replaces `content.labelNote` when not `undefined`, so a note field being
   * edited on the label screen previews live before the save lands. */
  noteOverride?: string | null;
  size?: "sm" | "lg";
  /**
   * The printed label's CONTAINER field carries a capacity/type clause (e.g.
   * "55 gal Drum") alongside the container code, but these are
   * `WasteContainer` fields, not `LabelContent` ones -- `content` above has
   * no way to carry them. Optional so this component stays pure: callers
   * that don't have them (or don't need the clause) simply omit them.
   *
   * `containerType` also decides the DOT marking group (`dotMarkingGroupFor`)
   * -- whether this container takes the package hazard design/subsidiary
   * design/orientation arrows, or the bulk/portable-tank ID-number marking
   * instead -- which is why the DOT block below needs it too, not just the
   * CONTAINER field.
   */
  containerType?: string | null;
  capacity?: number | null;
  capacityUnit?: UnitType | null;
  /**
   * Renders the preview as a fixed-size physical artifact -- a constant
   * pixel width per `size`, on its own white surface with a shadow, with its
   * header/diamond/QR/right-column proportions and (for `size="sm"`) its
   * field text sizes derived directly from pdf-generator's reference-scale
   * numbers -- instead of flexing to fill its container at MUI's default
   * type scale. The container label screen's live preview sits in a
   * scrollable "stage" and must read as what will actually print; off by
   * default so ContainerLabelSection's inline summary keeps its prior,
   * container-flexed rendering.
   */
  fixedWidth?: boolean;
}

/** A pure, presentational recreation of the printed container label — the
 * live preview for the label screen (Task 4), export dialog (Task 5), and
 * regenerate confirmation (Task 6). */
export function LabelPreview(props: LabelPreviewProps): ReactElement {
  const {
    content,
    layout,
    branded,
    noteOverride,
    size = "lg",
    containerType,
    capacity,
    capacityUnit,
    fixedWidth = false,
  } = props;
  // 49 CFR 171.8 defines bulk by how much the packaging holds, so the type
  // code alone cannot answer it. physicalState rides on the label content,
  // capacity on the container.
  const markingContainer: DotMarkingContainer | undefined = containerType
    ? { containerType, capacity, capacityUnit, physicalState: content.physicalState }
    : undefined;
  const effectiveNote = noteOverride === undefined ? content.labelNote : noteOverride;
  const labelWidth = LABEL_WIDTH_BY_SIZE[size];
  // pdf-generator's own scale factor for its labelMetrics(pageWidth) --
  // mirrored here so the preview's proportions track the PDF's at any label
  // width, not just the 4x6 reference scale (scale === 1 there).
  const scale = labelWidth / LABEL_REFERENCE_WIDTH;

  const rightColumnWidth = fixedWidth ? labelWidth * RIGHT_COLUMN_WIDTH_RATIO : undefined;
  const diamondSize = fixedWidth
    ? Math.round(DIAMOND_SIZE_AT_REFERENCE * scale)
    : DEFAULT_DIAMOND_SIZE;
  // The subsidiary design, orientation arrows, RCRA pictogram strip, and the
  // diamond's own class number are SVG/text elements sized by an explicit
  // pixel prop -- unlike the other text roles below (which fall back to
  // MUI's default type scale when unstyled), they need a real number whether
  // or not this preview is a fixed-size artifact. Scaling by the diamond's
  // own default:reference ratio when not fixedWidth keeps their proportions
  // consistent with `diamondSize` in that case too.
  const diamondScale = fixedWidth ? scale : DEFAULT_DIAMOND_SIZE / DIAMOND_SIZE_AT_REFERENCE;
  const subsidiaryDiamondSize = Math.round(SUBSIDIARY_DIAMOND_SIZE_AT_REFERENCE * diamondScale);
  const orientationArrowsSize = Math.round(ORIENTATION_ARROWS_SIZE_AT_REFERENCE * diamondScale);
  const rcraPictogramSize = Math.round(RCRA_PICTOGRAM_SIZE_AT_REFERENCE * diamondScale);
  const diamondNumberSize = Math.round(DIAMOND_NUMBER_SIZE_AT_REFERENCE * diamondScale);
  const inhalationTextSize = INHALATION_HAZARD_SIZE_AT_REFERENCE * diamondScale;
  const qrSize = fixedWidth ? Math.round(QR_SIZE_AT_REFERENCE * scale) : QR_SIZE_BY_SIZE[size];
  const headerMetrics: HeaderMetrics | undefined =
    fixedWidth && rightColumnWidth
      ? {
          titleSize: HEADER_TITLE_SIZE_AT_REFERENCE * scale,
          leadSize: HEADER_WARNING_LEAD_SIZE_AT_REFERENCE * scale,
          remainderSize: HEADER_WARNING_REMAINDER_SIZE_AT_REFERENCE * scale,
          paddingVertical: HEADER_PADDING_VERTICAL_AT_REFERENCE * scale,
          warningBlockMaxWidth: rightColumnWidth,
        }
      : undefined;
  // Only the 4x6 ("sm") preview gets PDF-mirrored text sizes this pass --
  // Letter's own sizing is unchanged (see LabelPreviewProps.fixedWidth and
  // the QA round-2/3 log this fix responds to). scale === 1 at "sm", so
  // every field below is numerically identical to its _AT_REFERENCE constant
  // -- kept as `* scale` (not the bare constant) so this stays correct if
  // LABEL_WIDTH_BY_SIZE.sm ever stops being exactly LABEL_REFERENCE_WIDTH.
  const smTextMetrics: SmTextMetrics | undefined =
    fixedWidth && size === "sm"
      ? {
          fieldLabelSize: FIELD_LABEL_SIZE_AT_REFERENCE * scale,
          fieldLabelLetterSpacing: FIELD_LABEL_LETTER_SPACING_AT_REFERENCE * scale,
          valueSize: VALUE_SIZE_AT_REFERENCE * scale,
          chipTextSize: CHIP_TEXT_SIZE_AT_REFERENCE * scale,
          idShippingNameSize: ID_SHIPPING_NAME_SIZE_AT_REFERENCE * scale,
          secondaryLineSize: SECONDARY_LINE_SIZE_AT_REFERENCE * scale,
          diamondNumberSize: DIAMOND_NUMBER_SIZE_AT_REFERENCE * scale,
          captionSize: CAPTION_SIZE_AT_REFERENCE * scale,
        }
      : undefined;

  return (
    <>
      <Box
        className="container-label-preview"
        data-testid="label-preview"
        sx={
          fixedWidth
            ? {
                // QR_ONLY has no header/columns to fill the label's full
                // width -- QrOnlyLayoutBody's own padding (p: 3) around the QR
                // block is all the breathing room this surface needs, so it
                // shrink-wraps to its content instead of carrying the FULL
                // layout's fixed labelWidth and leaving dead horizontal space
                // on either side of a small QR.
                width: layout === LabelFormat.QrOnly ? "fit-content" : labelWidth,
                flexShrink: 0,
                backgroundColor: "common.white",
                boxShadow: 2,
                borderRadius: 1,
              }
            : undefined
        }
      >
        {layout === LabelFormat.Full ? (
          <FullLayoutBody
            content={content}
            branded={branded}
            qrSize={qrSize}
            diamondSize={diamondSize}
            subsidiaryDiamondSize={subsidiaryDiamondSize}
            orientationArrowsSize={orientationArrowsSize}
            rcraPictogramSize={rcraPictogramSize}
            diamondNumberSize={diamondNumberSize}
            inhalationTextSize={inhalationTextSize}
            rightColumnWidth={rightColumnWidth}
            headerMetrics={headerMetrics}
            smTextMetrics={smTextMetrics}
            containerType={containerType}
            markingContainer={markingContainer}
            capacity={capacity}
            capacityUnit={capacityUnit}
          />
        ) : (
          <QrOnlyLayoutBody
            content={content}
            branded={branded}
            qrSize={qrSize}
            smTextMetrics={smTextMetrics}
          />
        )}
        {/* The QR-only PDF's QrOnlyPage renders only the branded code -- no
            note -- so the preview must not show one either; showing it here
            would preview text that never actually prints. */}
        {layout === LabelFormat.Full ? (
          <NoteRow note={effectiveNote} smTextMetrics={smTextMetrics} />
        ) : null}
      </Box>
    </>
  );
}

/**
 * The notices that describe pages the canvas cannot show: the bulk
 * ID-number marking, and the full-size hazard-class label(s).
 *
 * Rendered by `LabelPreviewStage` OUTSIDE its zoom wrapper, not by
 * `LabelPreview` inside it. The label is a fixed-size artifact and the stage
 * zooms it to fit, so anything inside that wrapper is scaled with the
 * artwork -- at a 0.2 fit these sentences rendered at an effective 2.8px.
 * They are chrome about the label, not part of it, so they hold their own
 * size.
 */
export function LabelPreviewNotices(props: LabelPreviewProps): ReactElement | null {
  const { content, layout, containerType, capacity, capacityUnit } = props;
  const markingContainer: DotMarkingContainer | undefined = containerType
    ? { containerType, capacity, capacityUnit, physicalState: content.physicalState }
    : undefined;
  const markingGroup = markingContainer ? dotMarkingGroupFor(markingContainer) : undefined;
  const showBulkMarkingNotice =
    layout === LabelFormat.Full &&
    markingGroup !== undefined &&
    requiresLargeIdNumber(markingGroup) &&
    Boolean(content.dotIdNumber);
  const packageMarkingInstruction = resolvePackageMarkingInstruction(
    layout,
    content,
    markingContainer
  );
  if (!showBulkMarkingNotice && !packageMarkingInstruction) {
    return null;
  }
  return (
    <>
      {showBulkMarkingNotice && markingGroup ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          This container also gets a second page: the ID number at 3.9 inches in black on an orange
          panel. {bulkMarkingInstruction(markingGroup, markingContainer)}
        </Alert>
      ) : null}
      <PackageMarkingNotice instruction={packageMarkingInstruction} />
    </>
  );
}

/**
 * The package placement instruction (49 CFR 172.406(c)) when this content
 * would print full-size hazard-class label page(s) the canvas cannot show
 * inline, or undefined otherwise. FULL layout only -- QR_ONLY carries no DOT
 * block at all.
 */
function resolvePackageMarkingInstruction(
  layout: LabelFormat,
  content: LabelContentFieldsFragment,
  markingContainer: DotMarkingContainer | undefined
): string | undefined {
  if (layout !== LabelFormat.Full || isDotProfileIncomplete(content)) {
    return undefined;
  }
  const dot = resolveDotBlock(content, markingContainer);
  if (!dot.showHazardLabels || !(dot.primaryLabel || dot.subsidiaryLabel)) {
    return undefined;
  }
  return dot.markingInstruction;
}

/** The notice below the canvas for a PACKAGE container whose hazard-class
 * label page(s) print outside the 4x6 canvas -- mirrors the bulk marking
 * Alert above it. */
function PackageMarkingNotice({
  instruction,
}: {
  instruction: string | undefined;
}): ReactElement | null {
  if (!instruction) {
    return null;
  }
  return (
    <Alert severity="info" sx={{ mt: 2 }}>
      This container also gets additional page(s): the full-size hazard-class label(s), each at
      least 100mm on a side. {instruction}
    </Alert>
  );
}

function NoteRow({
  note,
  smTextMetrics,
}: {
  note?: string | null;
  smTextMetrics?: SmTextMetrics;
}): ReactElement | null {
  if (!note) {
    return null;
  }
  return (
    <Box data-testid="label-preview-note" sx={{ borderTop: "1px solid #000000", px: 2, py: 1 }}>
      {/* The PDF's note text has no size of its own -- it inherits the
          page's base fontSize, set to `leftMetrics.valueSize`
          (hazardousWasteLabel.tsx) -- so it mirrors `valueSize` here too. */}
      <Typography
        variant="body2"
        sx={{ fontSize: smTextMetrics ? `${smTextMetrics.valueSize}px` : undefined }}
      >
        {note}
      </Typography>
    </Box>
  );
}

function LabelHeader({
  metrics,
  content,
}: {
  metrics?: HeaderMetrics;
  content: LabelContentFieldsFragment;
}): ReactElement {
  const header = labelHeaderFor(
    content.wasteKind,
    content.classification,
    content.universalWasteCategory
  );
  const hasLegend = headerCarriesLegend(header);
  // Mirrors pdf-generator: the printed title box is fixed width so the
  // marking never wraps, and the longer markings scale down to fit rather
  // than being abbreviated. Scaled here by the same ratio so the preview
  // shows the proportion that will actually print -- which means solving
  // against the box the PDF actually uses, wider when the marking has the
  // row to itself (headerTitleBoxWidth), not the narrow one every time.
  // Solved at reference scale so the ratio is scale-invariant, then applied
  // to whatever size this preview is rendering at.
  const titleScale =
    fitHeaderTitleSize(
      header.title,
      headerTitleBoxWidth(header, HEADER_TITLE_WIDTH_AT_REFERENCE, RIGHT_COLUMN_WIDTH_AT_REFERENCE),
      HEADER_TITLE_SIZE_AT_REFERENCE
    ) / HEADER_TITLE_SIZE_AT_REFERENCE;
  return (
    <Box
      sx={{
        backgroundColor: HEADER_COLOR,
        color: HEADER_TEXT_COLOR,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 2,
        py: metrics ? `${metrics.paddingVertical}px` : 1,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          // Mirrors pdf-generator: with the legend the marking sits in its own
          // left-hand box; without it, it owns the row and centres.
          flexGrow: hasLegend ? 0 : 1,
          textAlign: hasLegend ? "left" : "center",
          // Only the fixed-width previews shrink the marking to fit (the
          // scale above needs metrics to apply to), so only they can promise
          // one line. Without metrics, wrapping is what keeps a long marking
          // inside the header instead of running under the legend block.
          whiteSpace: metrics ? "nowrap" : undefined,
          fontSize: metrics ? `${metrics.titleSize * titleScale}px` : undefined,
        }}
      >
        {header.title}
      </Typography>
      {/* Fixed-width and right-aligned, mirroring pdf-generator's
          rightColumnWidth-sized warning block (LabelHeader in
          hazardousWasteLabelComponents.tsx) -- without a cap here, this
          block's default MUI type sizes ran wide enough to spill toward the
          title instead of reading as the export's compact right-aligned
          block. flexShrink: 0 keeps the title's own flexGrow spacer (below)
          from squeezing it before this cap ever binds. */}
      {/* Only where the 262.32 legend applies -- see labelHeaderFor. */}
      {header.warningLead && header.warningRemainder ? (
        <Box sx={{ textAlign: "right", maxWidth: metrics?.warningBlockMaxWidth, flexShrink: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              display: "block",
              lineHeight: 1.2,
              fontSize: metrics ? `${metrics.leadSize}px` : undefined,
            }}
          >
            {header.warningLead}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontSize: metrics ? `${metrics.remainderSize}px` : "0.65rem",
            }}
          >
            {header.warningRemainder}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

/** "Flammable Gas" from "FLAMMABLE GAS" — the shared HAZARD_CLASS_DIAMOND
 * table stores every design name upper-cased; mirrors the pdf-generator
 * lambda's own `titleCase` so the preview's class line reads the same way
 * the printed label's does. */
function titleCase(value: string): string {
  const capitalize = (part: string): string =>
    part.length > 0 ? part[0].toUpperCase() + part.slice(1) : part;
  return value
    .toLowerCase()
    .split(" ")
    .map((word) => word.split("-").map(capitalize).join("-"))
    .join(" ");
}

/** "Class 3 Flammable Liquid · PG II" — omits the design name when the class
 * has no HAZARD_CLASS_DIAMOND entry, and the packing-group clause when the
 * shared `packingGroupClause` suppresses it (unset or NONE), exactly as the
 * printed label's `secondaryDotLine` does. Takes the UNGATED diamond entry
 * (mirrors pdf-generator's `resolveDotBlock`), so a division-unknown class
 * ("2", "6") still prints "Class 2 Gases" even though it gets no design. */
function classAndPackingGroupLine(
  content: LabelContentFieldsFragment,
  diamond: HazardClassDiamondEntry | undefined
): string | undefined {
  const diamondName = diamond ? titleCase(diamond.name) : undefined;
  const classPart = content.dotHazardClass
    ? ["Class", content.dotHazardClass, diamondName].filter(Boolean).join(" ")
    : undefined;
  const packingGroupPart = packingGroupClause(content.dotPackingGroup);
  const line = [classPart, packingGroupPart].filter(Boolean).join(" · ");
  return line.length > 0 ? line : undefined;
}

/** Everything the DOT block needs, derived once from `content` and
 * `containerType`. Mirrors pdf-generator's `resolveDotBlock`
 * (hazardousWasteLabelComponents.tsx) -- kept as its own function, not
 * inlined into `DotHazardBlock`, for the same reason the PDF's is: growing
 * `DotHazardBlock` with this many gates would push it toward the complexity
 * ceiling. */
interface DotBlockInfo {
  showHazardLabels: boolean;
  primaryLabel: HazardClassDiamondEntry | undefined;
  subsidiaryLabel: HazardClassDiamondEntry | undefined;
  inhalationLine: string | undefined;
  showOrientationArrows: boolean;
  markingInstruction: string | undefined;
  classAndPackingGroupLine: string | undefined;
}

/**
 * The placement instruction for whichever second surface this container's
 * label points at: the package hazard-label placement (49 CFR 172.406(c))
 * when a hazard design prints, or the bulk/portable-tank ID-number placement
 * when that marking prints instead. Mirrors pdf-generator's
 * `resolveMarkingInstruction` (hazardousWasteLabelComponents.tsx) -- the two
 * are mutually exclusive, so at most one branch below ever applies.
 */
function resolveMarkingInstruction(
  content: LabelContentFieldsFragment,
  markingGroup: DotMarkingGroup | undefined,
  showHazardLabels: boolean,
  markingContainer: DotMarkingContainer | undefined
): string | undefined {
  // Counted from the labels that actually print at full size, not from the
  // 19mm indication on this page: class 7 draws the trefoil here but takes no
  // regulated label (see regulatedLabelProblem), and an instruction to apply
  // one would point at a page that never prints.
  const regulated = [content.dotHazardClass, content.dotSubsidiaryHazardClass].filter(
    printsRegulatedHazardLabel
  ).length;
  if (showHazardLabels && regulated > 0) {
    return packageLabelInstruction(regulated === 2);
  }
  // Only where the ID-number page will actually print -- an instruction
  // about an ID number the stream does not have would point at nothing.
  if (markingGroup !== undefined && requiresLargeIdNumber(markingGroup) && content.dotIdNumber) {
    return bulkMarkingInstruction(markingGroup, markingContainer);
  }
  return undefined;
}

function resolveDotBlock(
  content: LabelContentFieldsFragment,
  markingContainer: DotMarkingContainer | undefined
): DotBlockInfo {
  const markingGroup = markingContainer ? dotMarkingGroupFor(markingContainer) : undefined;
  // Only a PACKAGE container takes the hazard-class label, the subsidiary
  // label, the inhalation wording and the orientation arrows. A bulk
  // container or portable tank takes the 3.9in ID number on its own page
  // instead (the Alert above), and an unrecognised container type takes no
  // design rather than a guessed one.
  const showHazardLabels = markingGroup !== undefined && requiresHazardLabel(markingGroup);
  // Gated on the PICTOGRAM, not just on the entry existing. "2" and "6" name
  // a class with no division, and a diamond with a number and a blank upper
  // half is not a DOT marking that exists -- those two keep the "Class 2
  // Gases" text line instead.
  const primaryEntry = content.dotHazardClass
    ? diamondForHazardClass(content.dotHazardClass)
    : undefined;
  const primaryLabel = primaryEntry?.design.pictogram ? primaryEntry : undefined;
  const subsidiaryEntry = content.dotSubsidiaryHazardClass
    ? diamondForHazardClass(content.dotSubsidiaryHazardClass)
    : undefined;
  const subsidiaryLabel = subsidiaryEntry?.design.pictogram ? subsidiaryEntry : undefined;
  const markingInstruction = resolveMarkingInstruction(
    content,
    markingGroup,
    showHazardLabels,
    markingContainer
  );
  return {
    showHazardLabels,
    primaryLabel,
    subsidiaryLabel,
    inhalationLine: poisonInhalationLine(content.dotHazardClass),
    // 49 CFR 172.312 -- LIQUID only. SLUDGE, GAS and POWDER are not "liquid"
    // for 172.312, so they take no arrows.
    showOrientationArrows: showHazardLabels && content.physicalState === PhysicalState.Liquid,
    markingInstruction,
    // The ungated primaryEntry, so a division-unknown class still prints
    // "Class 2 Gases".
    classAndPackingGroupLine: classAndPackingGroupLine(content, primaryEntry),
  };
}

/** The hazard-class design, the subsidiary design, and the inhalation and
 * orientation-arrow marks that ride with them -- the part of the DOT block
 * that only a PACKAGE container takes (`dot.showHazardLabels`). Mirrors
 * pdf-generator's `DotBlockHazardLabels`. */
function DotBlockHazardLabels({
  content,
  dot,
  diamondSize,
  subsidiaryDiamondSize,
  orientationArrowsSize,
  diamondNumberSize,
  inhalationTextSize,
}: {
  content: LabelContentFieldsFragment;
  dot: DotBlockInfo;
  diamondSize: number;
  subsidiaryDiamondSize: number;
  orientationArrowsSize: number;
  diamondNumberSize: number;
  inhalationTextSize?: number;
}): ReactElement | null {
  if (!dot.showHazardLabels) {
    return null;
  }
  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        {dot.primaryLabel ? (
          <PreviewHazardLabel
            design={dot.primaryLabel.design}
            hazardClass={content.dotHazardClass ?? undefined}
            size={diamondSize}
            numberSize={diamondNumberSize}
          />
        ) : null}
        {/* 49 CFR 172.402(b): the subsidiary label shows the symbol and no
            class number, so no `hazardClass` is passed. */}
        {dot.subsidiaryLabel ? (
          <PreviewHazardLabel
            design={dot.subsidiaryLabel.design}
            size={subsidiaryDiamondSize}
            numberSize={0}
          />
        ) : null}
      </Stack>
      {dot.inhalationLine ? (
        <Typography
          sx={{
            fontWeight: 700,
            textAlign: "center",
            mt: 0.5,
            fontSize: inhalationTextSize ? `${inhalationTextSize}px` : undefined,
          }}
        >
          {dot.inhalationLine}
        </Typography>
      ) : null}
      {dot.showOrientationArrows ? <OrientationArrows size={orientationArrowsSize} /> : null}
    </>
  );
}

function DotHazardBlock({
  content,
  markingContainer,
  diamondSize,
  subsidiaryDiamondSize,
  orientationArrowsSize,
  diamondNumberSize,
  inhalationTextSize,
  smTextMetrics,
}: {
  content: LabelContentFieldsFragment;
  markingContainer?: DotMarkingContainer;
  diamondSize: number;
  subsidiaryDiamondSize: number;
  orientationArrowsSize: number;
  diamondNumberSize: number;
  inhalationTextSize?: number;
  smTextMetrics?: SmTextMetrics;
}): ReactElement | null {
  if (isDotProfileIncomplete(content)) {
    return null;
  }
  const dot = resolveDotBlock(content, markingContainer);
  const idAndShippingName = [content.dotIdNumber, content.dotProperShippingName]
    .filter(Boolean)
    .join(" · ");

  return (
    <Stack alignItems="center" sx={{ mb: 1 }}>
      <DotBlockHazardLabels
        content={content}
        dot={dot}
        diamondSize={diamondSize}
        subsidiaryDiamondSize={subsidiaryDiamondSize}
        orientationArrowsSize={orientationArrowsSize}
        diamondNumberSize={diamondNumberSize}
        inhalationTextSize={inhalationTextSize}
      />
      {idAndShippingName ? (
        <Typography
          sx={{
            fontWeight: 700,
            textAlign: "center",
            mt: 0.5,
            fontSize: smTextMetrics ? `${smTextMetrics.idShippingNameSize}px` : undefined,
          }}
        >
          {idAndShippingName}
        </Typography>
      ) : null}
      {dot.classAndPackingGroupLine ? (
        <Typography
          variant="body2"
          sx={{
            color: SECONDARY_TEXT_COLOR,
            textAlign: "center",
            mt: 0.25,
            fontSize: smTextMetrics ? `${smTextMetrics.secondaryLineSize}px` : undefined,
          }}
        >
          {dot.classAndPackingGroupLine}
        </Typography>
      ) : null}
      {/* The marking page itself (not shown here -- see the Alert above the
          canvas) is the digits alone, so the placement instruction has to
          live on the label. Absent for a package container, which takes the
          labels above instead of a marking page. */}
      {dot.markingInstruction ? (
        <Typography
          variant="body2"
          sx={{
            color: SECONDARY_TEXT_COLOR,
            textAlign: "center",
            mt: 0.25,
            fontSize: inhalationTextSize ? `${inhalationTextSize}px` : undefined,
          }}
        >
          {dot.markingInstruction}
        </Typography>
      ) : null}
    </Stack>
  );
}

function TentLogoOverlay(): ReactElement {
  return (
    <Box
      data-testid="label-preview-tent-logo"
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "22%",
        height: "22%",
        backgroundColor: KNOCKOUT_BACKGROUND_COLOR,
        borderRadius: "20%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img src={EncampLogo} alt="" style={{ width: "70%", height: "70%" }} />
    </Box>
  );
}

function QrBlock({
  content,
  branded,
  qrSize,
  smTextMetrics,
}: {
  content: LabelContentFieldsFragment;
  branded: boolean;
  qrSize: number;
  smTextMetrics?: SmTextMetrics;
}): ReactElement {
  return (
    <Stack alignItems="center">
      <Box sx={{ position: "relative", width: qrSize, height: qrSize }}>
        <img
          src={qrImageUrl(content.qrUrl)}
          alt="Container label QR code"
          width={qrSize}
          height={qrSize}
        />
        {branded ? <TentLogoOverlay /> : null}
      </Box>
      {/* Just the container label -- no truncation/shrink logic needed for
          it, and at captionSize (7.5pt at the 4x6 reference scale) a
          realistic container label fits the right column's width on one
          line without help. `whiteSpace: nowrap` is a safety net on top of
          that, not a truncation scheme the PDF doesn't have. */}
      <Typography
        data-testid="label-preview-qr-caption"
        variant="caption"
        sx={{
          color: MUTED_TEXT_COLOR,
          mt: 0.5,
          fontSize: smTextMetrics ? `${smTextMetrics.captionSize}px` : undefined,
          whiteSpace: smTextMetrics ? "nowrap" : undefined,
        }}
      >
        {content.containerLabel}
      </Typography>
    </Stack>
  );
}

function FullLayoutBody({
  content,
  branded,
  qrSize,
  diamondSize,
  subsidiaryDiamondSize,
  orientationArrowsSize,
  rcraPictogramSize,
  diamondNumberSize,
  inhalationTextSize,
  rightColumnWidth,
  headerMetrics,
  smTextMetrics,
  containerType,
  markingContainer,
  capacity,
  capacityUnit,
}: {
  content: LabelContentFieldsFragment;
  branded: boolean;
  qrSize: number;
  diamondSize: number;
  subsidiaryDiamondSize: number;
  orientationArrowsSize: number;
  rcraPictogramSize: number;
  diamondNumberSize: number;
  inhalationTextSize?: number;
  /** Fixed right-column width (mirrors the pdf-generator lambda's own
   * `rightColumnWidth` proportion) -- set only when the preview renders as a
   * fixed-size artifact; otherwise the column just wraps the QR with a
   * little breathing room, as before. */
  rightColumnWidth?: number;
  headerMetrics?: HeaderMetrics;
  smTextMetrics?: SmTextMetrics;
  containerType?: string | null;
  markingContainer?: DotMarkingContainer;
  capacity?: number | null;
  capacityUnit?: UnitType | null;
}): ReactElement {
  const rightColumnSx = rightColumnWidth ? { width: rightColumnWidth } : { minWidth: qrSize + 32 };
  return (
    <>
      <LabelHeader metrics={headerMetrics} content={content} />
      <Box sx={{ display: "flex", gap: 2, p: 2 }}>
        <LeftColumn
          content={content}
          containerType={containerType}
          markingContainer={markingContainer}
          capacity={capacity}
          capacityUnit={capacityUnit}
          smTextMetrics={smTextMetrics}
          rcraPictogramSize={rcraPictogramSize}
        />
        <Box sx={{ width: "1px", backgroundColor: RULE_COLOR }} />
        <Stack alignItems="center" sx={rightColumnSx}>
          <DotHazardBlock
            content={content}
            markingContainer={markingContainer}
            diamondSize={diamondSize}
            subsidiaryDiamondSize={subsidiaryDiamondSize}
            orientationArrowsSize={orientationArrowsSize}
            diamondNumberSize={diamondNumberSize}
            inhalationTextSize={inhalationTextSize}
            smTextMetrics={smTextMetrics}
          />
          <QrBlock
            content={content}
            branded={branded}
            qrSize={qrSize}
            smTextMetrics={smTextMetrics}
          />
        </Stack>
      </Box>
    </>
  );
}

function QrOnlyLayoutBody({
  content,
  branded,
  qrSize,
  smTextMetrics,
}: {
  content: LabelContentFieldsFragment;
  branded: boolean;
  qrSize: number;
  smTextMetrics?: SmTextMetrics;
}): ReactElement {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
      <QrBlock content={content} branded={branded} qrSize={qrSize} smTextMetrics={smTextMetrics} />
    </Box>
  );
}
